import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import { supabase } from '../lib/supabase';
import type { FeaturedLocation } from '../lib/database.types';

export default function FeaturedLocationDetail() {
  const { slug } = useParams();
  const [loc, setLoc] = useState<FeaturedLocation | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    (async () => {
      const { data } = await supabase
        .from('ga_featured_locations')
        .select('*')
        .eq('slug', slug)
        .eq('is_active', true)
        .maybeSingle();
      if (!data) {
        setNotFound(true);
        return;
      }
      setLoc(data as FeaturedLocation);
    })();
  }, [slug]);

  if (notFound) {
    return (
      <>
        <Nav />
        <div style={{ padding: '120px 0', textAlign: 'center' }}>
          <h1 className="display" style={{ fontSize: 'clamp(40px, 6vw, 64px)' }}>Not here.</h1>
          <p className="muted">That location isn't featured on Goaround right now.</p>
          <Link to="/featured-locations" className="btn btn-ghost" style={{ marginTop: 24 }}>← Back to featured locations</Link>
        </div>
        <Footer />
      </>
    );
  }

  if (!loc) {
    return (
      <>
        <Nav />
        <div style={{ padding: '120px 0', textAlign: 'center', color: 'var(--ink-soft)' }}>Loading…</div>
      </>
    );
  }

  return (
    <>
      <Nav />
      <section style={{ paddingTop: 32, paddingBottom: 48 }}>
        <div className="container">
          <Link to="/featured-locations" className="mono" style={{ fontSize: 12, color: 'var(--ink-soft)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>← All featured locations</Link>
        </div>
      </section>

      <section style={{ paddingBottom: 48 }}>
        <div className="container">
          <div
            style={{
              width: '100%',
              aspectRatio: '16 / 7',
              borderRadius: 24,
              overflow: 'hidden',
              // Layered: image on top, gradient as fallback while photos are pending.
              background: loc.hero_image_url
                ? `url(${loc.hero_image_url}) center/cover no-repeat, linear-gradient(135deg, oklch(0.45 0.14 22), oklch(0.62 0.16 48))`
                : `linear-gradient(135deg, oklch(0.45 0.14 22), oklch(0.62 0.16 48))`,
            }}
          />
        </div>
      </section>

      <section style={{ paddingBottom: 96 }}>
        <div className="container">
          <div className="r-split">
            <div>
              {loc.neighborhood && <div className="eyebrow" style={{ marginBottom: 14 }}>{loc.neighborhood}</div>}
              <h1 className="display" style={{ fontSize: 'clamp(48px, 7vw, 88px)', margin: 0 }}>{loc.name}</h1>
              {loc.vibes.length > 0 && (
                <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {loc.vibes.map((v) => (
                    <span key={v} className="tag">{v}</span>
                  ))}
                </div>
              )}
              {loc.description && (
                <p style={{ marginTop: 28, fontSize: 18, lineHeight: 1.55, color: 'var(--ink-soft)', maxWidth: 640 }}>
                  {loc.description}
                </p>
              )}
              {loc.address && (
                <div style={{ marginTop: 32, padding: 24, borderRadius: 16, background: 'var(--card)' }}>
                  <div className="mono" style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-soft)', marginBottom: 8 }}>Address</div>
                  <div style={{ fontSize: 16 }}>{loc.address}</div>
                  {loc.category && (
                    <>
                      <div className="mono" style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-soft)', marginTop: 16, marginBottom: 8 }}>Type</div>
                      <div style={{ fontSize: 16 }}>{loc.category}</div>
                    </>
                  )}
                </div>
              )}
            </div>

            <aside style={{ position: 'sticky', top: 24, alignSelf: 'start' }}>
              <div style={{ padding: 28, borderRadius: 18, background: 'var(--ink)', color: 'var(--bg)' }}>
                <div className="mono" style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--primary)', marginBottom: 12 }}>Featured location</div>
                <div className="display" style={{ fontSize: 26, lineHeight: 1.1, marginBottom: 16 }}>
                  Want to book a team night here?
                </div>
                <p style={{ fontSize: 14, color: 'color-mix(in oklch, var(--bg) 70%, transparent)', margin: '0 0 20px', lineHeight: 1.5 }}>
                  Sign up to see live packages, available dates, and pricing across our full network.
                </p>
                <Link
                  to="/signup"
                  className="btn btn-pop"
                  style={{ width: '100%', justifyContent: 'center' }}
                >
                  View packages & plan your night →
                </Link>
                <div className="mono" style={{ marginTop: 14, fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'color-mix(in oklch, var(--bg) 55%, transparent)', textAlign: 'center' }}>
                  Free for teams · Magic link sign-in
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
}
